import type {
  Person,
  PersonWithDetails,
  PersonSection,
  PersonTag,
  PersonLink,
  CreatePersonRequest,
  UpdatePersonRequest,
  CreatePersonSectionRequest,
  UpdatePersonSectionRequest,
  CreatePersonLinkRequest,
  PersonSearchParams,
  ParsedPersonMarkdown,
} from '../types/person.js';

const BASE_URL = '/api/people';

async function fetchJson<T>(url: string, init?: RequestInit): Promise<T> {
  const response = await fetch(url, init);
  if (!response.ok) {
    const body = await response.json().catch(() => ({ error: response.statusText }));
    const msg = typeof body.error === 'string' ? body.error : body.error?.message ?? `Request failed: ${response.status}`;
    throw new Error(msg);
  }
  return response.json() as Promise<T>;
}

// People CRUD

export async function getPeople(params?: PersonSearchParams): Promise<Person[]> {
  const query = new URLSearchParams();
  if (params?.search) query.set('search', params.search);
  if (params?.category) query.set('category', params.category);
  if (params?.tag) query.set('tag', params.tag);
  if (params?.limit !== undefined) query.set('limit', String(params.limit));
  if (params?.offset !== undefined) query.set('offset', String(params.offset));
  const qs = query.toString();
  return fetchJson<Person[]>(qs ? `${BASE_URL}?${qs}` : BASE_URL);
}

export async function getPerson(id: number): Promise<PersonWithDetails> {
  return fetchJson<PersonWithDetails>(`${BASE_URL}/${id}`);
}

export async function createPerson(data: CreatePersonRequest): Promise<Person> {
  return fetchJson<Person>(BASE_URL, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  });
}

export async function updatePerson(id: number, data: UpdatePersonRequest): Promise<Person> {
  return fetchJson<Person>(`${BASE_URL}/${id}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  });
}

export async function deletePerson(id: number): Promise<void> {
  const response = await fetch(`${BASE_URL}/${id}`, { method: 'DELETE' });
  if (!response.ok) {
    const body = await response.json().catch(() => ({ error: response.statusText }));
    const msg = typeof body.error === 'string' ? body.error : body.error?.message ?? `Request failed: ${response.status}`;
    throw new Error(msg);
  }
}

// Sections

export async function addSection(personId: number, data: CreatePersonSectionRequest): Promise<PersonSection> {
  return fetchJson<PersonSection>(`${BASE_URL}/${personId}/sections`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  });
}

export async function updateSection(personId: number, sectionId: number, data: UpdatePersonSectionRequest): Promise<PersonSection> {
  return fetchJson<PersonSection>(`${BASE_URL}/${personId}/sections/${sectionId}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  });
}

export async function deleteSection(personId: number, sectionId: number): Promise<void> {
  const response = await fetch(`${BASE_URL}/${personId}/sections/${sectionId}`, { method: 'DELETE' });
  if (!response.ok) {
    const body = await response.json().catch(() => ({ error: response.statusText }));
    const msg = typeof body.error === 'string' ? body.error : body.error?.message ?? `Request failed: ${response.status}`;
    throw new Error(msg);
  }
}

export async function reorderSections(personId: number, orderedIds: number[]): Promise<void> {
  const response = await fetch(`${BASE_URL}/${personId}/sections/reorder`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ orderedIds }),
  });
  if (!response.ok) {
    const body = await response.json().catch(() => ({ error: response.statusText }));
    const msg = typeof body.error === 'string' ? body.error : body.error?.message ?? `Request failed: ${response.status}`;
    throw new Error(msg);
  }
}

// Tags

export async function addTag(personId: number, tag: string): Promise<PersonTag> {
  return fetchJson<PersonTag>(`${BASE_URL}/${personId}/tags`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ tag }),
  });
}

export async function deleteTag(personId: number, tag: string): Promise<void> {
  const response = await fetch(`${BASE_URL}/${personId}/tags/${encodeURIComponent(tag)}`, { method: 'DELETE' });
  if (!response.ok) {
    const body = await response.json().catch(() => ({ error: response.statusText }));
    const msg = typeof body.error === 'string' ? body.error : body.error?.message ?? `Request failed: ${response.status}`;
    throw new Error(msg);
  }
}

// Markdown Import

export async function parseMarkdownPreview(markdown: string): Promise<ParsedPersonMarkdown> {
  return fetchJson<ParsedPersonMarkdown>(`${BASE_URL}/parse-markdown`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ markdown }),
  });
}

export async function importFromMarkdown(markdown: string): Promise<PersonWithDetails> {
  return fetchJson<PersonWithDetails>(`${BASE_URL}/import-markdown`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ markdown }),
  });
}

// Links

export async function addLink(personId: number, data: CreatePersonLinkRequest): Promise<PersonLink> {
  return fetchJson<PersonLink>(`${BASE_URL}/${personId}/links`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  });
}

export async function deleteLink(personId: number, linkId: number): Promise<void> {
  const response = await fetch(`${BASE_URL}/${personId}/links/${linkId}`, { method: 'DELETE' });
  if (!response.ok) {
    const body = await response.json().catch(() => ({ error: response.statusText }));
    const msg = typeof body.error === 'string' ? body.error : body.error?.message ?? `Request failed: ${response.status}`;
    throw new Error(msg);
  }
}
